import { AuthApiClient } from "@/api/auth_client";
import { ToastProgrammatic as Toast } from "buefy";
import AuthPage from "@/views/AuthPage.vue";
import DashboardPage from "@/views/DashboardPage.vue";
import Home from "@/views/Home.vue";
import Login from "@/views/full-page/Login.vue";
import Vue from "vue";
import VueRouter, { Route } from "vue-router";

import {
  clearBreadcrumbs,
  setContentData,
  setTenantData,
  setUserData
} from "@/router/data";
import { isLoggedIn, loggedInGuard, loggedOutGuard } from "@/router/auth";
import multiguard from "vue-router-multiguard";

Vue.use(VueRouter);

const UUID4RegEx =
  "[0-9A-F]{8}-[0-9A-F]{4}-4[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}";

const routes = [
  {
    path: "/",
    name: "root",
    beforeEnter: (to: Route, from: Route, next: Function) => {
      // Either redirect to the root of the dashboard when the user is logged in,
      // or to the login page.
      isLoggedIn().then(isUserLoggedIn => {
        if (isUserLoggedIn) {
          next("/home");
        } else {
          next("/login");
        }
      });
    }
  },
  {
    path: "/logout",
    name: "logout",
    beforeEnter: (to: Route, from: Route, next: Function) => {
      // Performs a logout operation when visited
      const api = new AuthApiClient();
      api.logOut().finally(() => {
        Toast.open({
          type: "is-info",
          message: "Du hast dich erfolgreich ausgeloggt.",
          duration: 3000,
          queue: false
        });
        next("/");
      });
    }
  },
  {
    path: "",
    component: AuthPage,
    beforeEnter: loggedOutGuard,
    children: [
      {
        path: "/login",
        name: "login",
        component: Login
      },
      {
        path: "/signup",
        name: "signup",
        component: () =>
          import(
            /* webpackChunkName: "signup" */ "@/views/full-page/Signup.vue"
          )
      },
      {
        path: "/activate",
        name: "activate",
        component: () =>
          import(
            /* webpackChunkName: "signup" */ "@/views/full-page/Activate.vue"
          )
      },
      {
        path: "/reset-password",
        name: "reset-password",
        component: () =>
          import(
            /* webpackChunkName: "reset-password" */ "@/views/full-page/ResetPassword.vue"
          )
      },
      {
        path: "/confirm-password",
        name: "confirm-password",
        component: () =>
          import(
            /* webpackChunkName: "confirm-password" */ "@/views/full-page/ConfirmPassword.vue"
          )
      }
    ]
  },
  {
    path: "",
    component: DashboardPage,
    beforeEnter: multiguard([loggedInGuard, setTenantData, clearBreadcrumbs]),
    children: [
      {
        path: "/home",
        name: "home",
        beforeEnter: multiguard([clearBreadcrumbs]),
        component: Home
      },
      {
        path: `/tenant/:tenant_id(${UUID4RegEx})/open_transfers`,
        name: "tenant-open-bank-transfers",
        beforeEnter: multiguard([clearBreadcrumbs]),
        component: () =>
          import(
            /* webpackChunkName: "tenant-open-bank-transfers" */ "@/views/tenant/BankTransfers.vue"
          )
      },
      {
        path: `/tenant/:tenant_id(${UUID4RegEx})/users`,
        name: "tenant-users",
        beforeEnter: multiguard([clearBreadcrumbs]),
        component: () =>
          import(
            /* webpackChunkName: "tenant-users" */ "@/views/tenant/Users.vue"
          )
      },
      {
        path: `/tenant/:tenant_id(${UUID4RegEx})/user/:user_id`,
        name: "tenant-user-details",
        beforeEnter: multiguard([setUserData, clearBreadcrumbs]),
        component: () =>
          import(
            /* webpackChunkName: "tenant-user-details" */ "@/views/tenant/UserDetails.vue"
          )
      },
      {
        path: `/tenant/:tenant_id(${UUID4RegEx})/videos`,
        name: "tenant-videos",
        beforeEnter: multiguard([clearBreadcrumbs]),
        component: () =>
          import(
            /* webpackChunkName: "tenant-videos" */ "@/views/tenant/Videos.vue"
          )
      },
      {
        path: `/tenant/:tenant_id(${UUID4RegEx})/video/new_video`,
        name: "tenant-new-video",
        beforeEnter: multiguard([clearBreadcrumbs]),
        component: () =>
          import(
            /* webpackChunkName: "tenant-new-video" */ "@/views/tenant/CreateVideo.vue"
          )
      },
      {
        path: `/tenant/:tenant_id(${UUID4RegEx})/video/:content_id(${UUID4RegEx})`,
        name: "tenant-video-details",
        beforeEnter: multiguard([setContentData, clearBreadcrumbs]),
        component: () =>
          import(
            /* webpackChunkName: "tenant-video-details" */ "@/views/tenant/VideoDetails.vue"
          )
      },
      {
        path: `/tenant/:tenant_id(${UUID4RegEx})`,
        name: "tenant-overview",
        beforeEnter: multiguard([clearBreadcrumbs]),
        component: () =>
          import(
            /* webpackChunkName: "tenant-overview" */ "@/views/tenant/Overview.vue"
          )
      }
    ]
  }
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes: routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  }
});
export default router;
